import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, RefreshCw } from '../../../icons';
import { ConsoleListScreen } from '../../../components/console-list/ConsoleListScreen';
import { ConsoleListTable } from '../../../components/console-list/ConsoleListTable';
import {
  fetchAiUsageRollup, fetchAiQuota, saveAiQuota,
  type AiUsageRollup, type AiUsageBucket, type AiUsageUnit, type AiInstitutionQuota,
} from '../../../api/aiProviders';
import { getNumberLocale } from '../../../lib/base-i18n';

/**
 * 관리 콘솔 › AI › **AI 사용량** (판 K).
 *
 * 2026-09-10 사용자 지시: 「총사용량, 회사별 사용량을 집계하는게 필요하다, 모델별로도 집계가 필요함」.
 *
 * ## ★★★ 이 숫자는 «잰 것의 합»이지 «전부»가 아니다
 *
 * 계량은 provider 가 토큰 수를 알려 줬을 때만 쌓인다(관문 G1 의 규칙 — 모르는 것을 0 으로
 * 적지 않는다). CLI 처럼 안 알려 주는 갈래가 섞이면 실제보다 적게 보인다.
 * 화면이 그 사실을 적어야 관리자가 이 숫자를 청구서로 오해하지 않는다.
 *
 * ## ★★ 「기록 없음」과 「0」을 가른다
 *
 * 줄이 없는 축은 **그리지 않는다.** 0 으로 채우면 「안 썼다」와 「기록이 없다」가 섞인다.
 *
 * ## ★ 모델을 안 알려 준 것은 «(모름)»
 *
 * 빈 모델 이름은 「모델이 없다」가 아니라 「provider 가 안 알려 줬다」는 뜻이다.
 */

/** 판 L — 「년, 월, 주, 일」(사용자 지시). 기간은 단위가 정한다(년이면 3년치). */
const UNITS: Array<{ key: AiUsageUnit; label: string }> = [
  { key: 'day', label: '일' },
  { key: 'week', label: '주' },
  { key: 'month', label: '월' },
  { key: 'year', label: '년' },
];

/* ⚠ 로케일을 못 박지 않는다 — 8443 쪽 가드가 `toLocale*` 의 하드코딩 BCP-47 을 막고 있고,
     같은 규율을 콘솔에서도 지킨다(여기엔 아직 그 가드가 없다). */
function fmt(n: number): string {
  return n.toLocaleString(getNumberLocale());
}

/** 가장 큰 줄을 100% 로 본 막대 — 눈으로 크기를 견주게 한다. */
function Bar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <span className="aiusg__bar" aria-hidden="true">
      <span className="aiusg__barfill" style={{ width: `${pct}%` }} />
    </span>
  );
}

function BucketTable({ title, rows, empty }: { title: string; rows: AiUsageBucket[]; empty: string }) {
  const max = rows.reduce((m, r) => Math.max(m, r.promptTokens + r.completionTokens), 0);
  return (
    <section className="aiusg__sec">
      <h3 className="aiusg__h">{title}</h3>
      <ConsoleListTable<AiUsageBucket>
        rows={rows}
        rowKey={(r) => r.key || '(none)'}
        empty={<span>{empty}</span>}
        columns={[
          { key: 'label', header: '이름', render: (r) => <span className="cl-name">{r.label}</span> },
          {
            key: 'tokens',
            header: '토큰',
            render: (r) => (
              <span>
                {fmt(r.promptTokens + r.completionTokens)}
                <Bar value={r.promptTokens + r.completionTokens} max={max} />
              </span>
            ),
          },
          { key: 'in', header: '입력', render: (r) => <span>{fmt(r.promptTokens)}</span> },
          { key: 'out', header: '출력', render: (r) => <span>{fmt(r.completionTokens)}</span> },
          { key: 'calls', header: '호출', render: (r) => <span>{fmt(r.calls)}회</span> },
        ]}
      />
    </section>
  );
}

/**
 * 판 M — **회사별 한도** (시스템 관리자). 「관리콘솔에서 시스템 관리자는 회사별 토큰량을
 * 수정할 수 있다」(2026-09-10 사용자 지시).
 *
 * ★★★ 넘어도 **막지 않는다** — 계량이 CLI 를 못 재기 때문이다. 화면이 알릴 뿐이다.
 * ★ **비우면 무제한**이다 — 0 은 「한 톨도 못 쓴다」라 다른 뜻이다.
 */
function QuotaTable(): JSX.Element {
  const [rows, setRows] = useState<AiInstitutionQuota[] | null>(null);
  const [month, setMonth] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setErr(null);
    try {
      const d = await fetchAiQuota();
      setRows(d.institutions);
      setMonth(d.month);
      setEdits(Object.fromEntries(d.institutions.map((i) => [i.institutionId, i.limit ?? ''])));
    } catch {
      setRows(null);
      setErr('한도를 불러오지 못했습니다.');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const save = async (id: string): Promise<void> => {
    const raw = (edits[id] ?? '').replace(/[,\s]/g, '').trim();
    const n = raw === '' ? null : Number(raw);
    if (n !== null && (!Number.isFinite(n) || n < 0)) { setErr('숫자를 적어 주세요.'); return; }
    setBusy(true);
    setErr(null);
    try {
      await saveAiQuota(id, n === null ? null : Math.floor(n));
      await load();
    } catch {
      setErr('저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="aiusg__sec">
      <h3 className="aiusg__h">회사별 한도 ({month})</h3>
      <p className="cl-empty" style={{ padding: 0, textAlign: 'left' }}>
        ★ 한도를 넘어도 <b>대화가 막히지는 않습니다</b> — 계량은 provider 가 토큰 수를 알려 줬을 때만
        쌓이고, 명령줄(CLI) 연결은 안 알려 주는 경우가 있어 막으면 그쪽만 예외가 됩니다.
        <b> 비우면 무제한</b>입니다.
      </p>
      {err !== null && (
        <div className="admin-console__alert" role="alert">
          <AlertTriangle size={16} aria-hidden="true" /><span>{err}</span>
        </div>
      )}
      <ConsoleListTable<AiInstitutionQuota>
        rows={rows ?? []}
        rowKey={(r) => r.institutionId}
        empty={<span>{err === null ? '읽는 중…' : '보여 줄 것이 없습니다.'}</span>}
        columns={[
          { key: 'name', header: '회사', render: (r) => <span className="cl-name">{r.name}</span> },
          {
            key: 'used',
            header: '이번 달 사용',
            render: (r) => (
              <span className={r.over ? 'cl-badge' : undefined}>
                {fmt(r.usedThisMonth)}
                {r.percent !== null && <span className="cl-chip">{r.percent}%</span>}
                {r.over && <span className="cl-chip">넘음</span>}
              </span>
            ),
          },
          {
            key: 'limit',
            header: '한 달 한도',
            render: (r) => (
              <span onClick={(e) => e.stopPropagation()} className="aiusg__qedit">
                <input
                  value={edits[r.institutionId] ?? ''}
                  onChange={(e) => setEdits((p) => ({ ...p, [r.institutionId]: e.target.value }))}
                  placeholder="비우면 무제한"
                  inputMode="numeric"
                  aria-label={`${r.name} 한 달 한도`}
                  disabled={busy}
                />
                <button type="button" className="svc-btn" disabled={busy} onClick={() => { void save(r.institutionId); }}>
                  저장
                </button>
              </span>
            ),
          },
          {
            key: 'md',
            header: '사원 기본',
            /* 회사 관리자가 정하는 값이라 «보기»만 한다 — 여기서 고치면 두 곳이 된다. */
            render: (r) => <span>{r.memberDefault === null ? '무제한' : fmt(Number(r.memberDefault))}</span>,
          },
        ]}
      />
    </section>
  );
}

export function AiUsageSection(): JSX.Element {
  const [unit, setUnit] = useState<AiUsageUnit>('day');
  const [data, setData] = useState<AiUsageRollup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (u: AiUsageUnit) => {
    setBusy(true);
    setError(null);
    try {
      setData(await fetchAiUsageRollup(u));
    } catch {
      setData(null);
      setError('사용량을 불러오지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { void load(unit); }, [load, unit]);

  const total = data?.total;
  const totalTokens = total ? total.promptTokens + total.completionTokens : 0;
  const maxBucket = useMemo(
    () => (data?.series ?? []).reduce((m, d) => Math.max(m, d.tokens), 0),
    [data],
  );

  return (
    <ConsoleListScreen
      title="AI 사용량"
      kpi={data === null ? '—' : `${fmt(totalTokens)} 토큰 · ${fmt(total!.calls)}회`}
      desc="연결이 실제로 쓴 양입니다. ★ provider 가 토큰 수를 알려 줬을 때만 쌓이므로 «잰 것의 합»이지 «전부»가 아닙니다 — 알려 주지 않는 갈래(CLI 등)는 빠져 있을 수 있습니다."
      /* ★ 기간은 좌 분류에 두지 않는다 — 그 자리는 «거르개»고 항목마다 숫자를 요구한다.
           기간에는 셀 것이 없어 0 을 적으면 「0건」으로 읽힌다. 그래서 툴바에 둔다. */
      toolsExtra={(
        <>
          <span className="cl-chipset" role="group" aria-label="묶는 단위">
            {UNITS.map((u) => (
              <button
                key={u.key}
                type="button"
                className={`cl-chip${unit === u.key ? ' is-on' : ''}`}
                aria-pressed={unit === u.key}
                onClick={() => setUnit(u.key)}
              >
                {u.label}
              </button>
            ))}
          </span>
          <button type="button" className="svc-btn" disabled={busy} onClick={() => { void load(unit); }}>
            <RefreshCw size={14} aria-hidden="true" /> {busy ? '읽는 중…' : '새로 고침'}
          </button>
        </>
      )}
      notice={error !== null ? (
        <div className="admin-console__alert" role="alert">
          <AlertTriangle size={16} aria-hidden="true" /><span>{error}</span>
        </div>
      ) : null}
    >
      {data === null ? (
        <p className="cl-empty">{busy ? '읽는 중…' : '보여 줄 것이 없습니다.'}</p>
      ) : (
        <div className="aiusg">
          {/* 총합 — 세 숫자. 「합계와 항목의 합이 안 맞는」 일이 없게 한 번에 받은 값이다. */}
          <section className="aiusg__tot">
            <div className="aiusg__kpi">
              <span className="aiusg__kpil">총 토큰</span>
              <b className="aiusg__kpiv">{fmt(totalTokens)}</b>
            </div>
            <div className="aiusg__kpi">
              <span className="aiusg__kpil">입력 / 출력</span>
              <b className="aiusg__kpiv">{fmt(total!.promptTokens)} / {fmt(total!.completionTokens)}</b>
            </div>
            <div className="aiusg__kpi">
              <span className="aiusg__kpil">호출</span>
              <b className="aiusg__kpiv">{fmt(total!.calls)}회</b>
            </div>
            <div className="aiusg__kpi">
              <span className="aiusg__kpil">기간</span>
              <b className="aiusg__kpiv">{data.from} 부터 {data.days}일</b>
            </div>
          </section>

          {/* 기간별 추이 — 줄이 없는 구간은 그리지 않는다(0 으로 채우면 「안 썼다」가 된다). */}
          {data.series.length > 0 && (
            <section className="aiusg__sec">
              <h3 className="aiusg__h">{UNITS.find((u) => u.key === unit)?.label ?? ''}별 추이</h3>
              <div className="aiusg__days">
                {data.series.map((d) => (
                  <div key={d.bucket} className="aiusg__day" title={`${d.bucket} — ${fmt(d.tokens)} 토큰 · ${d.calls}회`}>
                    <Bar value={d.tokens} max={maxBucket} />
                    <span className="aiusg__daylbl">{unit === 'year' ? d.bucket : d.bucket.slice(2)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 판 M — 회사별 한도(시스템 관리자가 정한다). */}
          <QuotaTable />

          <BucketTable title="회사별" rows={data.byInstitution} empty="아직 기록이 없습니다." />
          <BucketTable title="모델별" rows={data.byModel} empty="아직 기록이 없습니다." />
          <BucketTable title="에이전트별" rows={data.byAgent} empty="아직 기록이 없습니다." />
          <BucketTable title="연결별" rows={data.byConnection} empty="아직 기록이 없습니다." />
        </div>
      )}
    </ConsoleListScreen>
  );
}
